<template>
  <div class="email-preview-container">
    <b-card :class="{ 'p-0': !showFilters }">
      <b-card-title class="mb-2" :class="{ 'px-0 pt-0': !showFilters }">
        {{ $t('operational_security.evaluation.email_preview') }}
      </b-card-title>

      <div v-if="isLoading" class="text-center py-5" :class="{ 'px-2': !showFilters }">
        <b-spinner variant="primary" label="Cargando..."></b-spinner>
        <p class="mt-2 text-muted">{{ $t('loading') }}</p>
      </div>

      <!-- Error State -->
      <b-alert v-else-if="error" variant="danger" show>
        <div class="alert-body">
          <feather-icon icon="AlertTriangleIcon" class="mr-50" />
          <span>{{ error }}</span>
        </div>
      </b-alert>

      <!-- Email Content -->
      <div v-else-if="emailHtml" class="email-preview-content" :class="{ 'p-1': !showFilters }">
        <div class="email-frame-wrapper">
          <iframe
            ref="emailFrame"
            :srcdoc="emailHtml"
            frameborder="0"
            class="email-iframe"
            @load="onIframeLoad"
          ></iframe>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-5" :class="{ 'px-2': !showFilters }">
        <feather-icon icon="MailIcon" size="48" class="text-muted mb-2" />
        <p class="text-muted">{{ $t('operational_security.evaluation.no_email_preview') }}</p>
      </div>
    </b-card>
  </div>
</template>

<script>
import { ref, onMounted, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'
import store from '@/store'

export default {
  name: 'EmailPreview',
  props: {
    evaluationId: {
      type: [Number, String],
      required: true
    },
    showFilters: {
      type: Boolean,
      default: true
    }
  },
  setup(props) {
    const toast = useToast()
    const emailHtml = ref('')
    const isLoading = ref(false)
    const error = ref(null)
    const emailFrame = ref(null)

    const isDark = computed(() => store.state.appConfig.layout.skin === 'dark')

    const fetchEmailPreview = async () => {
      if (!props.evaluationId) {
        error.value = 'ID de evaluación no proporcionado'
        return
      }

      isLoading.value = true
      error.value = null

      try {
        const response = await axios.get(
          `/v1/operational-security-forms/template-answers/emails/preview/${props.evaluationId}`
        )

        emailHtml.value = response.data

      } catch (err) {
        console.error('Error fetching email preview:', err)
        error.value = err.response?.data?.message || 'Error al cargar la vista previa del email'

        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.value,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    }

    const onIframeLoad = () => {
      if (emailFrame.value && emailFrame.value.contentWindow) {
        try {
          const iframeDoc = emailFrame.value.contentWindow.document
          const height = iframeDoc.body.scrollHeight
          emailFrame.value.style.height = `${height + 20}px`
        } catch (e) {
          emailFrame.value.style.height = '600px'
        }
      }
    }

    onMounted(() => {
      fetchEmailPreview()
    })

    return {
      emailHtml,
      isLoading,
      error,
      emailFrame,
      isDark,
      onIframeLoad,
      fetchEmailPreview
    }
  }
}
</script>

<style lang="scss" scoped>
.email-preview-container {
  .email-preview-content {
    background: #f8f8f8;
    border-radius: 0.357rem;
    padding: 1rem;
  }

  .email-frame-wrapper {
    background: white;
    border-radius: 0.357rem;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    overflow: hidden;
  }

  .email-iframe {
    width: 100%;
    min-height: 400px;
    display: block;
    border: none;
  }
}
</style>
